/* Copyright (c) 2017 Backpack Health LLC */

/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable import/no-unresolved */
/* eslint-disable global-require */
/* global window, document, IS_ANDROID_BUILD */

import { addLocaleData } from 'react-intl';
import * as _ from 'lodash';
import DateUtil from '../../../utilities/DateUtil';
import { types as userTypes, actions as userActions } from './user';
import AppUtils from '../../../utilities/AppUtils';
import { actions as systemNotificationActions } from './systemNotification';

const types = {
  SET_DEFAULT_CLIENT_LOCALE: 'app/locale/SET_DEFAULT_CLIENT_LOCALE',
  SET_CLIENT_LOCALES: 'app/locale/SET_CLIENT_LOCALES',
  ADD_LOCALE: 'app/locale/ADD_LOCALE',
  SET_WINDOW_TITLE_PHRASE: 'app/locale/SET_WINDOW_TITLE_PHRASE',
};

const initialState = {
  languageMessages: {},
  clientLocales: [],
  defaultClientLocale: 'en',
  windowTitlePhrase: { id: '', values: null },
};

export default function reducer(state = initialState, action = {}) {
  switch (action.type) {
    case userTypes.SET_LOCALE:
    case types.ADD_LOCALE:
      return _.assign({}, state, {
        languageMessages: _.assign({}, state.languageMessages, {
          [action.payload.languageCode]: action.payload.languageMessages,
        }),
      });
    case types.SET_CLIENT_LOCALES:
    case types.SET_DEFAULT_CLIENT_LOCALE:
    case types.SET_WINDOW_TITLE_PHRASE:
      return _.assign({}, state, action.payload);
    default:
      return state;
  }
}

function addRequestedLocaleData(reactIntlLocaleData, languageCode) {
  const d = reactIntlLocaleData.locale ? reactIntlLocaleData : Object.keys(reactIntlLocaleData).find((a => a.locale === languageCode))
  if (reactIntlLocaleData.locale) {
    addLocaleData(d);
  } else {
    Object.entries(reactIntlLocaleData).forEach(([, data]) => {
      addLocaleData(data);
    });
  }
}

export function loadLocale(localeData) {
  return new Promise((resolve) => {
    switch (localeData.language_code) {
      case 'es':
        import(/* webpackChunkName: "intl-es-react-intl" */ 'react-intl/locale-data/es').then(reactIntlLocaleData => {
          addRequestedLocaleData(reactIntlLocaleData, localeData.language_code);
        }).then(() => {
          import(/* webpackChunkName: "intl-es-messages" */ '../../../locales/es.json').then(languageMessages => {
            resolve(languageMessages);
          });
        });
        import(/* webpackChunkName: "intl-es-moment" */ 'moment/locale/es');
        break;
      case 'fr':
        import(/* webpackChunkName: "intl-fr-react-intl" */ 'react-intl/locale-data/fr').then(reactIntlLocaleData => {
          addRequestedLocaleData(reactIntlLocaleData, localeData.language_code);
        }).then(() => {
          import(/* webpackChunkName: "intl-fr-messages" */ '../../../locales/fr.json').then(languageMessages => {
            resolve(languageMessages);
          });
        });
        import(/* webpackChunkName: "intl-fr-moment" */ 'moment/locale/fr');
        break;
      case 'de':
        import(/* webpackChunkName: "intl-de-react-intl" */ 'react-intl/locale-data/de').then(reactIntlLocaleData => {
          addRequestedLocaleData(reactIntlLocaleData, localeData.language_code);
        }).then(() => {
          import(/* webpackChunkName: "intl-de-messages" */ '../../../locales/de.json').then(languageMessages => {
            resolve(languageMessages);
          });
        });
        import(/* webpackChunkName: "intl-de-moment" */ 'moment/locale/de');
        break;
      case 'pt':
        import(/* webpackChunkName: "intl-pt-react-intl" */ 'react-intl/locale-data/pt').then(reactIntlLocaleData => {
          addRequestedLocaleData(reactIntlLocaleData, localeData.language_code);
        }).then(() => {
          import(/* webpackChunkName: "intl-pt-messages" */ '../../../locales/pt.json').then(languageMessages => {
            resolve(languageMessages);
          });
        });
        import(/* webpackChunkName: "intl-pt-moment" */ 'moment/locale/pt');
        break;
      case 'it':
        import(/* webpackChunkName: "intl-it-react-intl" */ 'react-intl/locale-data/it').then(reactIntlLocaleData => {
          addRequestedLocaleData(reactIntlLocaleData, localeData.language_code);
        }).then(() => {
          import(/* webpackChunkName: "intl-it-messages" */ '../../../locales/it.json').then(languageMessages => {
            resolve(languageMessages);
          });
        });
        import(/* webpackChunkName: "intl-it-moment" */ 'moment/locale/it');
        break;
      case 'en':
      default:
        import(/* webpackChunkName: "intl-en-react-intl" */ 'react-intl/locale-data/en').then(reactIntlLocaleData => {
          addRequestedLocaleData(reactIntlLocaleData, localeData.language_code);
        }).then(() => {
          import(/* webpackChunkName: "intl-en-messages" */ '../../../locales/en.json').then(languageMessages => {
            resolve(languageMessages);
          });
        });
        break;
    }
  });
}

function setCurrentLocale(localeData) {
  return (dispatch) => {
    return loadLocale(localeData)
    .then((languageMessages) => {
      DateUtil.setLocale(localeData.language_code);
      dispatch(userActions.setLocale(localeData, languageMessages));
    }).catch(() => {
      dispatch(systemNotificationActions.showNotification('error.api.404-not-found.message'));
    });
  };
}

function addLocale(localeData, preferredLanguageCode, cb = () => {}) {
  return (dispatch) => {
    loadLocale(localeData)
    .then((languageMessages) => {
      // moment.js sets global locale when requiring new locale, so need to reset it to preferred
      DateUtil.setLocale(preferredLanguageCode);

      dispatch({
        type: types.ADD_LOCALE,
        payload: {
          languageCode: localeData.language_code,
          languageMessages,
        },
      });

      cb();
    }).catch(() => {
      dispatch(systemNotificationActions.showNotification('error.api.404-not-found.message'));
    });
  };
}

function setDefaultClientLocale(defaultClientLocale) {
  return {
    type: types.SET_DEFAULT_CLIENT_LOCALE,
    payload: {
      defaultClientLocale,
    },
  };
}

function setClientLocales(clientLocales) {
  return {
    type: types.SET_CLIENT_LOCALES,
    payload: {
      clientLocales,
    },
  };
}

function setWindowTitlePhrase(windowTitlePhrase) {
  return {
    type: types.SET_WINDOW_TITLE_PHRASE,
    payload: {
      windowTitlePhrase,
    },
  };
}

function initializeClientLocales() {
  let languages = window.navigator.languages ? window.navigator.languages : [];
  if (languages.length === 0) {
    languages = window.navigator.language ? [window.navigator.language] : [window.navigator.browserLanguage];
  }

  // default language based on marketing web referrer lang setting
  const knownReferrerDomain = 'backpackhealth.com';
  const referrerOrigin = document.referrer && typeof window.URL === 'function' ? new window.URL(document.referrer).origin : '';
  if (referrerOrigin.endsWith(knownReferrerDomain)) {
    const localeRegexp = new RegExp(`(.*${knownReferrerDomain}/)([a-z]{2})/.*`);
    const match = localeRegexp.exec(document.referrer);
    if (match && match.length > 2) {
      languages = [match[2]];
    }
  }

  const searchParams = AppUtils.getQueryParams(window.location.search);
  if (searchParams.lang) {
    languages = [searchParams.lang];
  }

  if (IS_ANDROID_BUILD) {
    return (dispatch) => {
      if (window.navigator.globalization) {
        window.navigator.globalization.getPreferredLanguage(({ value }) => {
          dispatch(setClientLocales([value]));
        }, () => {
          dispatch(setClientLocales(languages));
        });
      } else {
        dispatch(setClientLocales(languages));
      }
    };
  } else {
    return setClientLocales(languages);
  }
}

export const actions = {
  setCurrentLocale,
  setWindowTitlePhrase,
  addLocale,
  setDefaultClientLocale,
  initializeClientLocales,
};

export const selectors = {
  getMessages: (state, languageCode) => state.locale.languageMessages[languageCode] || {},
  getDefaultClientLocale: state => state.locale.defaultClientLocale,
  getClientLocales: state => state.locale.clientLocales,
  getWindowTitlePhrase: state => state.locale.windowTitlePhrase,
};
